/* eslint-disable @typescript-eslint/naming-convention */

interface ProductProperties {
	_location?: string
	[key: string]: any
}

interface SelectedProduct {
	variantId: string
	quantity: number
	category?: string
	type: 'product' | 'addOns'
	properties?: ProductProperties
	renderData?: any
}

interface PriceConfig {
	strategy: 'fixed_pricing' | 'dynamic_pricing'
	value: number
}

interface AddOnConfig {
	minimum: number
	maximum: number
}

interface OptionV2 {
	title: string
	 
	variant_id: string
	quantity: number
	 
	add_on: AddOnConfig
	 
	additional_info: any[]
	price: PriceConfig
}

interface CategoryProduct {
	id: string
	variants: Record<string, number>
}

interface Category {
	title: string
	quantity: number
	products: CategoryProduct[]
}

interface Config {
	 
	bundle_id: string
	products: string[]
	categories: Category[]
	 
	options_v2: OptionV2[]
	 
	add_on_products: string[]
	 
	add_on_product_properties: Record<
		string,
		{
			variants: Record<string, number>
		}
	>
	settings: {
		 
		add_on: AddOnConfig
		price: PriceConfig
	}
}

export default {
	name: 'foxsellBundle',
	component({ config }: { config: Config }) {
		return {
			loading: false,
			bundleId: config.bundle_id || window.foxsell?.bundleConfig?.id,
			selectedProducts: [] as SelectedProduct[],
			minimumProducts: config.options_v2.reduce(
				(acc: number, option: any) => (option.quantity < acc ? option.quantity : acc),
				config.options_v2[0].quantity
			),
			maximumProducts: config.options_v2.reduce(
				(acc: number, option: any) => (option.quantity > acc ? option.quantity : acc),
				config.options_v2[0].quantity
			),

			qtyInCart() {
				return this.selectedProducts.reduce((acc, product) => acc + product.quantity, 0)
			},

			availableQtyToAdd() {
				return this.maximumProducts - this.qtyInCart()
			},

			getSortedOptions(ascending = false) {
				// Sort options in descending order
				return config.options_v2.sort((a: any, b: any) => (ascending ? a.quantity - b.quantity : b.quantity - a.quantity))
			},

			getSelectedOptionIndex() {
				const sortedOptions = this.getSortedOptions()
				const currentOptionIndex = sortedOptions.findIndex((option: any) => option.quantity <= this.qtyInCart())
				return currentOptionIndex
			},

			get selectedOption() {
				const currentOptionIndex = this.getSelectedOptionIndex()
				return this.getSortedOptions()[currentOptionIndex]
			},

			get nextOption() {
				const currentQty = this.qtyInCart()
				const sortedOptions = this.getSortedOptions(true)

				// If we're at or above the highest tier, return null
				if (currentQty >= sortedOptions[sortedOptions.length - 1].quantity) {
					return null
				}

				// Find the next tier above our current quantity
				return sortedOptions.find((option: any) => option.quantity > currentQty)
			},

			requiredQtyToProgressToNextOption() {
				return Math.max(0, (this.nextOption?.quantity || 0) - this.qtyInCart())
			},

			progress(optionIndex: number) {
				const sortedOptions = this.getSortedOptions(true)
				const currentQty = this.qtyInCart()
				const optionTarget = sortedOptions[optionIndex].quantity
				const previousOption = sortedOptions[optionIndex - 1]?.quantity || 0

				if (currentQty >= optionTarget) {
					return 100
				}

				if (currentQty <= previousOption) {
					return 0
				}

				const progress = ((currentQty - previousOption) / (optionTarget - previousOption)) * 100
				return Math.max(Math.min(progress, 100), 0)
			},

			canAddProduct() {
				return this.qtyInCart() < this.maximumProducts
			},

			canRemoveProduct(variantId: string) {
				const product = this.selectedProducts.find(p => p.variantId === variantId)
				return product ? product.quantity > 0 : false
			},

			canAddToCart() {
				const qty = this.qtyInCart()
				return qty >= this.minimumProducts && qty <= this.maximumProducts
			},

			addProduct(product: SelectedProduct, renderData?: any) {
				if (!this.canAddProduct()) return

				// Check if product already exists
				const existingIndex = this.selectedProducts.findIndex(p => p.variantId === product.variantId && p.type === product.type)

				if (existingIndex >= 0) {
					// Update existing product
					this.selectedProducts[existingIndex] = {
						...this.selectedProducts[existingIndex],
						quantity: this.selectedProducts[existingIndex].quantity + product.quantity
					}
				} else {
					// Add new product
					this.selectedProducts.push({
						...product,
						renderData
					})
				}
			},

			selectedProductsSubtotalCompareAt() {
				return this.selectedProducts.reduce((acc, product) => acc + product.renderData.priceFloat * product.quantity, 0)
			},

			selectedProductsSubtotal() {
				if (this.selectedOption?.price.strategy === 'dynamic_pricing') {
					return (this.selectedProductsSubtotalCompareAt() * (100 - this.selectedOption.price.value)) / 100
				}
				return this.selectedProductsSubtotalCompareAt()
			},

			updateProductQuantity(variantId: string, quantity: string) {
				const existingIndex = this.selectedProducts.findIndex(p => p.variantId === variantId)

				if (existingIndex >= 0) {
					this.selectedProducts[existingIndex].quantity = Math.min(parseInt(quantity, 10), this.availableQtyToAdd())
				}
			},

			removeProduct(variantId: string, removeAll = false) {
				const existingIndex = this.selectedProducts.findIndex(p => p.variantId === variantId)

				if (existingIndex >= 0) {
					// Reduce quantity by 1
					const updatedQuantity = removeAll ? 0 : this.selectedProducts[existingIndex].quantity - 1

					if (updatedQuantity <= 0) {
						// Remove product if quantity is 0 or less
						this.selectedProducts = this.selectedProducts.filter((_, index) => index !== existingIndex)
					} else {
						// Update quantity
						this.selectedProducts[existingIndex] = {
							...this.selectedProducts[existingIndex],
							quantity: updatedQuantity
						}
					}
				}
			},

			prepareAddToCartData() {
				const currentDate = Date.now()
				const identifier = `${this.bundleId}_${currentDate}`

				const addToCartFormData = { items: [] as any[] }

				// Preparing line Item Properties
				const bundleLineItemProperties = this.selectedProducts.map(product => {
					return {
						variantId: product.variantId,
						quantity: product.quantity,
						category: product.category,
						type: product.type,
						...(product.properties && { properties: product.properties })
					}
				})

				// Preparing Add to Cart Data
				addToCartFormData.items.push({
					id: this.selectedOption.variant_id,
					quantity: this.selectedOption.quantity,
					properties: {
						'__foxsell:dynamic_add_on_bundle_id': identifier,
						'__foxsell:dynamic_add_on_bundle_items': JSON.stringify(bundleLineItemProperties)
					}
				})

				return addToCartFormData
			},

			async addToCart() {
				if (this.loading) {
					return
				}
				this.loading = true
				console.log('addToCart called, current loading state:', this.loading)
				try {
					const addToCartFormData = this.prepareAddToCartData()
					const formattedItems = {
						items: addToCartFormData.items.map(item => ({
							id: item.id,
							quantity: 1,
							properties: item.properties
						}))
					}
					await window.liquidAjaxCart.add(formattedItems)
				} catch (error) {
					console.error('Error adding to cart:', error)
				} finally {
					this.loading = false
				}
			}
		}
	}
}
